/*
 * KindPagePaginated3.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-11-07 in huWaWi3 33.5.1
 * Copyright (c) 2023 HUDORA GmbH
 */

import { ICommandBarItemProps } from '@fluentui/react'
import { useSchema } from '@hudora/hd-react-components'
import { assertIsString } from 'assertate-debug'
import { DocumentNode } from 'graphql'
import { JSONSchema7 } from 'json-schema'
import React, { PropsWithChildren, ReactNode } from 'react'
import { Route } from 'wouter'
import { navigate } from 'wouter/use-location'

import { IEntity } from '../../../types'
import { EntitySelect2 } from '../EntitySelect'
import { KindListePaginated } from '../KindListePaginated'
import { IKindPageProps } from '../KindPage'
import { EntityPageWrapper3, IEntitySubPage3 } from './EntityPage3'

export interface IKindPage3Props {
  basePath: string
  componentPath: string
  /** Titel der Listen-Seite */
  title: string
  /** Jsonschema der dargestellten Entities */
  schemaName: string
  /** Paginated Query um die Liste darzustellen */
  listQuery: DocumentNode
  /** Liste der Spalten, die initial dargestellt werden */
  hideExcept?: string[]
  subPages?: IEntitySubPage3[]
  // Parameter, die sich auf eine einzelne Entity Page beziehen.
  renderEntityPage?: (entity) => ReactNode
  renderEntityPageEntity?: (entity, schemaName) => ReactNode
  renderEntityPageAnmerkung?: (entity, schemaName) => ReactNode
  renderEntityPageRightSidebar?: (entity, schemaName) => ReactNode
  renderEntityPageBottom?: (entity, schemaName) => ReactNode

  /** Query, um eine einzelne Entity zu laden.
   *
   * Bekommt den Parameter `nr` und sollte ein label `entity` haben. Also etwa so:
   *     query ($nr: ID!) { entity: hwKommiAuftrag2(designator: $nr) { id } }
   */
  entityQuery?: DocumentNode

  // initialLimit?: number // Anzahl der anfangs angezeigten Datensätzen
  // bulkEditKeyField?: string // bulkEditor aktivieren  listQuery: DocumentNode // GQL-Abfrage, um an die daten zu gelangen
  // listQueryOptions?: TListQueryOptions // GQL-Optionen für die List-Query
  createMutation?: DocumentNode
  createTemplate?: Partial<IEntity>
  // createPreset?: Record<string, any>
  mutation?: DocumentNode
  // nr?: string // Designator des übergeordneten Objekts
  entityCommandBarItems?: Array<ICommandBarItemProps>
  // entity?: IEntity // Übergeordnete Entity
  // noSort?: boolean // suppress sorting
  // className?: string

  /** Query für Suche und Suggestions */
  suggestionQuery?: DocumentNode

  rowToolsEnabled?: boolean
}

/** Rendert die Startseite für eine Kind (Datenbank-Tabelle/Modell)
 * mit einer Liste der Entities (Records/Instanzen) und der
 * Möglichkeit eine Entity (Records/Instanz) auszuwählen, um sie anzuzeigen.
 */
export function KindPagePaginated3(props: PropsWithChildren<IKindPage3Props>) {
  assertIsString(props.basePath, 'basePath missing')
  assertIsString(props.componentPath, 'componentPath missing')
  const { schema } = useSchema(props.schemaName)
  const newBasePath = `${props.basePath}/${props.componentPath}`
  return (
    <React.StrictMode>
      <section>
        <Route key="listPage3" path={newBasePath}>
          {KindPagePaginatedListe3(props, newBasePath, schema)}
        </Route>
        <Route key="entityPage3" path={`${newBasePath}/:nr+`}>
          {(params: any) => {
            const nr = params.nr.split('/')[0]
            return (
              <EntityPageWrapper3
                nr={nr}
                basePath={newBasePath}
                entityQuery={props.entityQuery}
                schemaName={props.schemaName}
                subPages={props.subPages}
                mutation={props.mutation}
                renderInner={props.renderEntityPage}
                commandBarItems={props.entityCommandBarItems}
                renderEntity={props.renderEntityPageEntity}
                renderAnmerkung={props.renderEntityPageAnmerkung}
                renderRightSidebar={props.renderEntityPageRightSidebar}
                renderBottom={props.renderEntityPageBottom}
                rowToolsEnabled={props.rowToolsEnabled}
              >
                {props.children}
              </EntityPageWrapper3>
            )
          }}
        </Route>
      </section>
    </React.StrictMode>
  )
}
function KindPagePaginatedListe3(props: IKindPageProps, newBasePath: string, schema: JSONSchema7) {
  const placeholder = (schema?.properties?.designator as JSONSchema7)?.title
  return (
    <>
      {props.suggestionQuery ? (
        <EntitySelect2
          onDesignatorSet={(nr) => {
            navigate(`${newBasePath}/${nr}`)
          }}
          schemaNames={[props.schemaName]}
        />
      ) : null}
      <KindListePaginated internalDesignatorLink={true} {...props} />
    </>
  )
}
